import React, { useMemo } from "react";
import { StyleSheet } from "react-native";
import Animated, {
  interpolate,
  interpolateColor,
  useAnimatedStyle,
  useDerivedValue,
} from "react-native-reanimated";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { useHeaderHeight } from "../../hooks/useHeaderHeight";
import { palette, spacing } from "../../styles";
import Block from "../Block";
import HeaderLeft from "../HeaderLeft";

type Props = {
  scrollY: Animated.SharedValue<number>;
  interpolateAddition: number;
  goBack: () => void;
};

const defaultHeight = 130;

const ParallaxHeader = ({ goBack, scrollY, interpolateAddition }: Props) => {
  const insets = useSafeAreaInsets();
  const navigationHeaderHeight = useHeaderHeight();

  const headerHeight = useMemo(
    () => navigationHeaderHeight + spacing.unit,
    [navigationHeaderHeight],
  );

  const opacity = useDerivedValue(() => {
    return interpolate(
      scrollY.value,
      [
        Math.max(defaultHeight - interpolateAddition, 0),
        Math.max(defaultHeight - interpolateAddition + spacing.unit * 8, spacing.unit),
      ],
      [0, 1],
    );
  });

  const containerStyle = useAnimatedStyle(() => ({
    backgroundColor: interpolateColor(
      opacity.value,
      [0, 1],
      [palette.common.transparent, palette.common.white],
    ),
    height: headerHeight + spacing.unit,
    paddingTop: insets.top - spacing.unit,
    elevation: opacity.value * 3,
  }));

  const borderStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
    backgroundColor: interpolateColor(
      opacity.value,
      [0, 1],
      [palette.common.transparent, palette.common.lightGray],
    ),
  }));

  return (
    <Animated.View style={[styles.container, containerStyle]}>
      <Block alignCenter justifySpace row paddingHorizontal={4}>
        <HeaderLeft containerStyle={styles.backIcon} onPress={goBack} />
      </Block>
      <Animated.View style={[styles.border, borderStyle]} />
    </Animated.View>
  );
};

export default React.memo(ParallaxHeader);

const styles = StyleSheet.create({
  backIcon: {
    marginLeft: -spacing.unit * 1.5,
    marginTop: -spacing.unit * 2.25,
  },
  border: {
    bottom: 0,
    height: spacing.border,
    position: "absolute",
    width: "100%",
  },
  container: {
    justifyContent: "center",
    position: "absolute",
    width: spacing.deviceWidth,
    zIndex: 10,
  },
});
